"use client";
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
  Modal,
  Select,
  FormControl,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Plus_Jakarta_Sans, Outfit } from "next/font/google";
import GoogleCaptchaWrapper from "../components/GoogleCaptchaWrapper"; // adjust path as needed
import { useTranslation } from "react-i18next";

const plusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});
const outfit = Outfit({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

// ── Dark card palette (navy, same as image) ────────────────────────────────
const CARD = {
  bg: "#1a1f36",
  inputBg: "rgba(255,255,255,0.07)",
  inputBorder: "rgba(255,255,255,0.15)",
  inputText: "#e5e7eb",
  placeholder: "rgba(255,255,255,0.35)",
  white: "#ffffff",
  purple: "#7c3aed",
  purpleHover: "#6d28d9",
  error: "#EF4444",
  muted: "rgba(255,255,255,0.55)",
};

// ── Dark-themed input (white text on navy) ─────────────────────────────────
const darkInput = (hasError, outfit) => ({
  mb: 2.5,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: CARD.inputBg,
    fontFamily: outfit.style.fontFamily,
    "& fieldset": { borderColor: hasError ? CARD.error : CARD.inputBorder },
    "&:hover fieldset": {
      borderColor: hasError ? CARD.error : "rgba(255,255,255,0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: hasError ? CARD.error : CARD.purple,
    },
  },
  "& .MuiInputBase-input": {
    color: CARD.inputText,
    fontFamily: outfit.style.fontFamily,
    padding: { xs: "8px 12px", sm: "9px 13px", md: "10px 14px" },
    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.875rem" },
  },
  "& .MuiInputBase-input::placeholder": { color: CARD.placeholder, opacity: 1 },
  "& .MuiFormHelperText-root": {
    position: "absolute",
    bottom: "-20px",
    left: 0,
    width: "100%",
    margin: 0,
    fontSize: { xs: "10px", sm: "11px", md: "12px" },
    color: CARD.error,
  },
});

const ArticleSixContact = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 9;
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  const [emailVerifying, setEmailVerifying] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    companyWebsite: "",
    phoneCountryCode: "+1",
    phoneNumber: "",
    hiringSlowdowns: [],
    hiringSlowdownsOther: "",
    monthlyHires: "",
    findingCandidates: [],
    screeningCandidates: [],
    screeningCandidatesOther: "",
    timeToHireValue: "",
    urgency: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });
  const [showHiringSlowdownsOther, setShowHiringSlowdownsOther] =
    useState(false);
  const [showScreeningCandidatesOther, setShowScreeningCandidatesOther] =
    useState(false);
  const [meetingBooked, setMeetingBooked] = useState(false);
  const [meetingDate, setMeetingDate] = useState(null);
  const [meetingTime, setMeetingTime] = useState(null);
  const prevStepRef = useRef(1);
  const [calIframeKey, setCalIframeKey] = useState(() => Date.now());
  const [showPickSlotHint, setShowPickSlotHint] = useState(false);
  const socketRef = useRef(null);

  // ── Cal.com iframe key reset ──────────────────────────────────────────────
  useEffect(() => {
    if (currentStep === 9 && prevStepRef.current !== 9) {
      setCalIframeKey(Date.now());
      setShowPickSlotHint(false);
    }
    prevStepRef.current = currentStep;
  }, [currentStep]);

  // ── WebSocket for booking confirmation ────────────────────────────────────
  useEffect(() => {
    if (currentStep !== 9 || meetingBooked || !formData.email?.trim()) return;
    const email = formData.email.trim().toLowerCase();
    (async () => {
      const { io } = await import("socket.io-client");
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const socket = io(origin, { path: "/socket.io" });
      socketRef.current = socket;
      const join = () => socket.emit("join_booking_room", { email });
      socket.on("connect", join);
      if (socket.connected) join();
      socket.on("booking_confirmed", (data) => {
        setMeetingDate(data.startTime);
        setMeetingTime(data.endTime || data.startTime);
        setMeetingBooked(true);
        sendConfirmationEmail(
          data.startTime,
          data.endTime || data.startTime,
        ).catch(console.error);
      });
    })();
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [currentStep, meetingBooked, formData.email]);

  // ── reCAPTCHA ──────────────────────────────────────────────────────────────
  useEffect(() => {
    window.onCaptchaChange = (token) => {
      if (token) {
        setCaptchaVerified(true);
        setCaptchaError("");
      } else setCaptchaVerified(false);
    };
    window.onCaptchaExpired = () => {
      setCaptchaVerified(false);
      setCaptchaError("reCAPTCHA has expired. Please verify again.");
    };
    window.onCaptchaError = () => {
      setCaptchaVerified(false);
      setCaptchaError("reCAPTCHA verification failed. Please try again.");
    };

    const renderRecaptcha = () => {
      if (typeof window !== "undefined" && window.grecaptcha?.render) {
        const el = document.querySelector(".g-recaptcha");
        if (el && !el.hasChildNodes()) {
          try {
            window.grecaptcha.render(el, {
              sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
              callback: "onCaptchaChange",
              "expired-callback": "onCaptchaExpired",
              "error-callback": "onCaptchaError",
            });
          } catch (e) {
            console.error("reCAPTCHA render error:", e);
          }
        }
      }
    };

    if (typeof window !== "undefined" && window.grecaptcha?.render)
      renderRecaptcha();
    else {
      const id = setInterval(() => {
        if (typeof window !== "undefined" && window.grecaptcha?.render) {
          renderRecaptcha();
          clearInterval(id);
        }
      }, 100);
      setTimeout(() => clearInterval(id), 10000);
    }
    return () => {
      delete window.onCaptchaChange;
      delete window.onCaptchaExpired;
      delete window.onCaptchaError;
    };
  }, []);

  // ── NeverBounce ───────────────────────────────────────────────────────────
  const verifyEmailWithNeverBounce = async (email) => {
    try {
      setEmailVerifying(true);
      const res = await fetch("/api/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      return await res.json();
    } catch (err) {
      console.error("[NeverBounce]", err);
      return { valid: true };
    } finally {
      setEmailVerifying(false);
    }
  };

  // ── Confirmation email ────────────────────────────────────────────────────
  const sendConfirmationEmail = async (date, time) => {
    try {
      const res = await fetch("/api/send-confirmation-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName,
          email: formData.email,
          meetingDate: date || meetingDate,
          meetingTime: time || meetingTime,
          companyWebsite: formData.companyWebsite || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success)
        console.error("Failed to send confirmation email:", data.message);
    } catch (e) {
      console.error("Error sending confirmation email:", e);
    }
  };

  // ── Validation (EXACT copy from ContactMain) ──────────────────────────────
  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.firstName.trim())
        newErrors.firstName = t("contactMain.validation.firstNameRequired");
      if (!formData.email.trim())
        newErrors.email = t("contactMain.validation.emailRequired");
      else if (
        !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
          formData.email.trim(),
        )
      )
        newErrors.email = t("contactMain.validation.emailInvalid");
      else if (
        /@(gmail|googlemail|yahoo|hotmail|outlook|live|icloud|aol|mail\.com|protonmail|zoho|yandex)\./i.test(
          formData.email.trim(),
        )
      )
        newErrors.email = t("contactMain.validation.emailWorkOnly");
    } else if (step === 2) {
      const phoneDigits = (formData.phoneNumber || "").replace(/\D/g, "");
      if (!formData.phoneNumber?.trim())
        newErrors.phoneNumber = t("contactMain.validation.phoneRequired");
      else if (phoneDigits.length < 10)
        newErrors.phoneNumber = t("contactMain.validation.phoneInvalid");
      const site = (formData.companyWebsite || "").trim();
      const urlRegex =
        /^(https?:\/\/)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*)(\.[a-zA-Z]{2,})+(:\d+)?(\/.*)?$/i;
      if (!site)
        newErrors.companyWebsite = t("contactMain.validation.websiteRequired");
      else if (!urlRegex.test(site))
        newErrors.companyWebsite = t("contactMain.validation.websiteInvalid");
    } else if (step === 3) {
      if (!formData.hiringSlowdowns.length)
        newErrors.hiringSlowdowns = t("contactMain.validation.selectOne");
      if (
        formData.hiringSlowdowns.includes("other") &&
        !formData.hiringSlowdownsOther.trim()
      )
        newErrors.hiringSlowdownsOther = t(
          "contactMain.validation.pleaseSpecify",
        );
    } else if (step === 4) {
      if (!formData.monthlyHires)
        newErrors.monthlyHires = t("contactMain.validation.selectOne");
    } else if (step === 5) {
      if (!formData.findingCandidates.length)
        newErrors.findingCandidates = t("contactMain.validation.selectOne");
    } else if (step === 6) {
      if (!formData.screeningCandidates.length)
        newErrors.screeningCandidates = t("contactMain.validation.selectOne");
      if (
        formData.screeningCandidates.includes("other") &&
        !formData.screeningCandidatesOther.trim()
      )
        newErrors.screeningCandidatesOther = t(
          "contactMain.validation.pleaseSpecify",
        );
    } else if (step === 7) {
      if (!formData.timeToHireValue)
        newErrors.timeToHireValue = t("contactMain.validation.selectOne");
    } else if (step === 8) {
      if (!formData.urgency)
        newErrors.urgency = t("contactMain.validation.selectOne");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── handleChange (EXACT copy from ContactMain) ────────────────────────────
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (type === "checkbox") {
      const currentArray = formData[name] || [];
      if (checked) {
        setFormData((prev) => ({ ...prev, [name]: [...currentArray, value] }));
        if (value === "other" && name === "hiringSlowdowns")
          setShowHiringSlowdownsOther(true);
        else if (value === "other" && name === "screeningCandidates")
          setShowScreeningCandidatesOther(true);
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: currentArray.filter((i) => i !== value),
        }));
        if (value === "other" && name === "hiringSlowdowns") {
          setShowHiringSlowdownsOther(false);
          setFormData((prev) => ({ ...prev, hiringSlowdownsOther: "" }));
        } else if (value === "other" && name === "screeningCandidates") {
          setShowScreeningCandidatesOther(false);
          setFormData((prev) => ({ ...prev, screeningCandidatesOther: "" }));
        }
      }
    } else {
      let nextValue = value;
      if (name === "phoneNumber")
        nextValue = (value || "")
          .replace(/\D/g, "")
          .replace(/^0+/, "")
          .slice(0, 15);
      setFormData((prev) => ({ ...prev, [name]: nextValue }));
      if (name === "phoneNumber" && currentStep === 2) {
        const digits = (nextValue || "").replace(/\D/g, "");
        setErrors((prev) => ({
          ...prev,
          phoneNumber: !digits
            ? t("contactMain.validation.phoneRequired")
            : digits.length < 10
              ? t("contactMain.validation.phoneInvalid")
              : "",
        }));
        return;
      }
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ── saveStepData (EXACT copy from ContactMain) ────────────────────────────
  const saveStepData = async (step) => {
    setLoading(true);
    try {
      const payload = {
        step,
        ...formData,
        email: formData.email,
        firstName: formData.firstName,
      };
      if (step === 2 && formData.phoneCountryCode && formData.phoneNumber) {
        const digits = (formData.phoneNumber || "")
          .replace(/\D/g, "")
          .replace(/^0+/, "");
        payload.phoneNumber = `${formData.phoneCountryCode} ${digits}`;
      }
      if (step === 1 && window.grecaptcha) {
        const token = window.grecaptcha.getResponse();
        if (!token) {
          setSubmitMessage({
            type: "error",
            text: t("contactMain.captchaRequired"),
          });
          setShowToast(true);
          setLoading(false);
          return false;
        }
        payload.token = token;
      }
      const res = await fetch("/api/save-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) return true;
      setSubmitMessage({
        type: "error",
        text: data.message || t("contactMain.saveFailed"),
      });
      setShowToast(true);
      return false;
    } catch {
      setSubmitMessage({ type: "error", text: t("contactMain.networkError") });
      setShowToast(true);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ── handleNext (EXACT copy from ContactMain) ──────────────────────────────
  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    if (currentStep === 1) {
      const nbResult = await verifyEmailWithNeverBounce(formData.email.trim());
      if (!nbResult.valid) {
        setErrors((prev) => ({ ...prev, email: nbResult.message }));
        return;
      }
    }
    const saved = await saveStepData(currentStep);
    if (!saved) return;
    const nextStep = Math.min(currentStep + 1, totalSteps);
    setCurrentStep(nextStep);
    if (nextStep === 9) await saveStepData(9);
  };

  // ── handleBack ────────────────────────────────────────────────────────────
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      if (currentStep === 2) {
        setCaptchaVerified(false);
        setCaptchaError("");
        setTimeout(() => {
          try {
            if (typeof window !== "undefined" && window.grecaptcha) {
              const el = document.querySelector(".g-recaptcha");
              if (el) {
                el.innerHTML = "";
                window.grecaptcha.render(el, {
                  sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
                  callback: "onCaptchaChange",
                  "expired-callback": "onCaptchaExpired",
                  "error-callback": "onCaptchaError",
                });
              }
            }
          } catch (e) {
            console.warn("reCAPTCHA re-render error:", e);
          }
        }, 300);
      }
    }
  };

  const handleCloseToast = (e, reason) => {
    if (reason === "clickaway") return;
    setShowToast(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleNext();
  };
  const isStep1Disabled = loading || emailVerifying || !captchaVerified;

  // ── Shared checkbox row ───────────────────────────────────────────────────
  const CheckRow = ({ label, checked, onChange }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 1.5,
        cursor: "pointer",
      }}
      onClick={onChange}
    >
      <Checkbox
        checked={checked}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
        sx={{
          color: "rgba(255,255,255,0.25)",
          "&.Mui-checked": { color: CARD.purple },
          p: 0.5,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontFamily: outfit.style.fontFamily,
          fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
          color: "rgba(255,255,255,0.85)",
          flex: 1,
          lineHeight: 1.5,
          wordBreak: "break-word",
          textAlign: isArabic ? "right" : "left",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <GoogleCaptchaWrapper>
      {/* Toast */}
      <Snackbar
        open={showToast}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={submitMessage.type}
          sx={{
            width: "100%",
            fontSize: { xs: "12px", sm: "14px", md: "16px" },
            fontFamily: outfit.style.fontFamily,
          }}
        >
          {submitMessage.text}
        </Alert>
      </Snackbar>

      {/* ── SECTION WRAPPER ─────────────────────────────────────────────── */}
      <Box
        id="contact"
        sx={{
          width: "100%",
          bgcolor: "#ffffff",
          py: { xs: 6, md: 10 },
          px: { xs: 3, sm: 6, md: 10, lg: 14 },
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 6, md: 10 },
          flexDirection: { xs: "column", md: "row" },
          boxSizing: "border-box",
          direction: isArabic ? "rtl" : "ltr",
        }}
      >
        {/* ── LEFT: plain white text ────────────────────────────────────── */}
        <Box sx={{ flex: 1, pt: { md: 1 } }}>
          <Typography
            sx={{
              fontFamily: plusJakartaSans.style.fontFamily,
              fontWeight: 800,
              fontSize: { xs: "1.4rem", sm: "1.65rem", md: "1.9rem" },
              color: "#111827",
              mb: 2.5,
              lineHeight: 1.25,
              textAlign: isArabic ? "right" : "left",
            }}
          >
            Ready, set, Article 6!
          </Typography>

          <Typography
            sx={{
              fontFamily: outfit.style.fontFamily,
              fontSize: { xs: "0.9rem", md: "0.975rem" },
              color: "#374151",
              mb: 2,
              lineHeight: 1.75,
              textAlign: isArabic ? "right" : "left",
            }}
          >
            Article 6 is thrilled to power your hiring as the official{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              AI Hiring Partner.
            </Box>
          </Typography>

          <Typography
            sx={{
              fontFamily: outfit.style.fontFamily,
              fontSize: { xs: "0.9rem", md: "0.975rem" },
              color: "#374151",
              mb: 2,
              lineHeight: 1.75,
              textAlign: isArabic ? "right" : "left",
            }}
          >
            Envision the future and seize the opportunity; this is the epicentre
            of innovation and growth for hiring teams and forward-thinking
            businesses.
          </Typography>

          <Typography
            sx={{
              fontFamily: outfit.style.fontFamily,
              fontSize: { xs: "0.9rem", md: "0.975rem" },
              color: "#374151",
              mb: 5,
              lineHeight: 1.75,
              textAlign: isArabic ? "right" : "left",
            }}
          >
            Grab a chance to meet our experts dedicated to building the digital
            hiring future in KSA and beyond. Explore cutting-edge solutions
            including AI screening, automated scheduling, and bias-free
            shortlisting to embrace bold transformations and unlock your true
            potential.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {[
              "Booth J30, Hall 2",
              "February 9–12, 2025",
              "Malham – Riyadh, Saudi Arabia",
            ].map((d) => (
              <Typography
                key={d}
                sx={{
                  fontFamily: outfit.style.fontFamily,
                  fontSize: "0.975rem",
                  color: "#374151",
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {d}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* ── RIGHT: dark navy card with full ContactMain form ─────────── */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: { xs: "100%", md: "48%", lg: "46%" },
            bgcolor: CARD.bg,
            borderRadius: "10px",
            p: { xs: 3, sm: 4, md: 5 },
            boxSizing: "border-box",
          }}
        >
          {/* Card header */}
          <Typography
            sx={{
              fontFamily: outfit.style.fontFamily,
              fontSize: "0.68rem",
              letterSpacing: 2,
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.4)",
              mb: 0.5,
            }}
          >
            EVENT
          </Typography>
          <Typography
            sx={{
              fontFamily: plusJakartaSans.style.fontFamily,
              fontWeight: 700,
              fontSize: { xs: "1.5rem", md: "1.9rem" },
              color: CARD.white,
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            Schedule a meeting
          </Typography>

          {/* Progress bar */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                width: "100%",
                height: "4px",
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${(currentStep / totalSteps) * 100}%`,
                  height: "100%",
                  bgcolor: CARD.purple,
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
          </Box>

          {/* ── FORM (exact ContactMain logic, dark styled) ── */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ width: "100%", minHeight: "360px" }}
          >
            {/* ── Step 1: First Name + Email + reCAPTCHA ── */}
            {currentStep === 1 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 1,
                    fontSize: {
                      xs: "1.25rem",
                      sm: "1.5rem",
                      md: "1.6rem",
                      lg: "1.2rem",
                      xl: "1.6rem",
                    },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step1.headline")}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.55)",
                    fontFamily: outfit.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "0.875rem", sm: "1rem", md: "1.1rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step1.subheadline")}
                </Typography>

                <Box sx={{ mb: 2, position: "relative" }}>
                  <Typography
                    sx={{
                      fontFamily: outfit.style.fontFamily,
                      fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.75)",
                      mb: 0.5,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("contactMain.step1.firstNameLabel")}{" "}
                    <Box component="span" sx={{ color: CARD.error }}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder={t("contactMain.step1.firstNamePlaceholder")}
                    disabled={loading || emailVerifying}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    inputProps={{
                      style: { textAlign: isArabic ? "right" : "left" },
                    }}
                    sx={darkInput(!!errors.firstName, outfit)}
                  />
                </Box>

                <Box sx={{ mb: 2, position: "relative" }}>
                  <Typography
                    sx={{
                      fontFamily: outfit.style.fontFamily,
                      fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.75)",
                      mb: 0.5,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("contactMain.step1.emailLabel")}{" "}
                    <Box component="span" sx={{ color: CARD.error }}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("contactMain.step1.emailPlaceholder")}
                    disabled={loading || emailVerifying}
                    error={!!errors.email}
                    helperText={errors.email}
                    inputProps={{ style: { textAlign: "left" } }}
                    sx={darkInput(!!errors.email, outfit)}
                  />
                </Box>

                {/* reCAPTCHA */}
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: {
                      xs: "center",
                      sm: "center",
                      md: isArabic ? "flex-end" : "flex-start",
                    },
                    "& .g-recaptcha": {
                      transform: {
                        xs: "scale(0.77)",
                        sm: "scale(0.85)",
                        md: "scale(0.85)",
                      },
                      transformOrigin: {
                        xs: "center center",
                        sm: "center center",
                        md: isArabic ? "right center" : "left center",
                      },
                    },
                  }}
                >
                  <div
                    className="g-recaptcha"
                    data-sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                    data-callback="onCaptchaChange"
                    data-expired-callback="onCaptchaExpired"
                    data-error-callback="onCaptchaError"
                  />
                </Box>
                {captchaError && (
                  <Typography
                    sx={{
                      color: CARD.error,
                      fontSize: { xs: "10px", sm: "11px", md: "12px" },
                      mb: 2,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {captchaError}
                  </Typography>
                )}
              </>
            )}

            {/* ── Step 2: Phone + Website ── */}
            {currentStep === 2 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 1,
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step2.headline", {
                    name:
                      formData.firstName?.trim() ||
                      t("contactMain.step2.there"),
                  })}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: outfit.style.fontFamily,
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                    color: CARD.muted,
                    mb: 3,
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step2.subheadline")}
                </Typography>

                {/* Phone label */}
                <Box
                  component="span"
                  sx={{
                    fontFamily: outfit.style.fontFamily,
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.75)",
                    mb: 0.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.25,
                  }}
                >
                  {t("contactMain.step2.phoneLabel")}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                  <Tooltip
                    title={t("contactMain.step2.phoneTooltip")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{
                        p: 0.25,
                        color: "rgba(255,255,255,0.4)",
                        "&:hover": { color: CARD.white },
                      }}
                      tabIndex={-1}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 2,
                    flexDirection: isArabic ? "row-reverse" : "row",
                  }}
                >
                  <FormControl
                    sx={{
                      width: 92,
                      minWidth: 92,
                      flexShrink: 0,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: CARD.inputBg,
                        fontFamily: outfit.style.fontFamily,
                        "& fieldset": { borderColor: CARD.inputBorder },
                      },
                    }}
                  >
                    <Select
                      name="phoneCountryCode"
                      value={formData.phoneCountryCode || "+1"}
                      onChange={handleChange}
                      disabled={loading}
                      size="medium"
                      sx={{
                        height: "56px",
                        "& .MuiSelect-select": {
                          py: 1.5,
                          fontSize: "0.875rem",
                          color: CARD.inputText,
                        },
                      }}
                    >
                      {[
                        "+1",
                        "+44",
                        "+91",
                        "+61",
                        "+49",
                        "+33",
                        "+81",
                        "+86",
                        "+55",
                        "+52",
                        "+971",
                        "+92",
                        "+31",
                        "+34",
                        "+39",
                        "+46",
                        "+27",
                        "+65",
                        "+60",
                        "+66",
                      ].map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder={t("contactMain.step2.phonePlaceholder")}
                    disabled={loading}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 15,
                      style: { textAlign: isArabic ? "right" : "left" },
                    }}
                    sx={darkInput(!!errors.phoneNumber, outfit)}
                  />
                </Box>

                {/* Website label */}
                <Box
                  component="span"
                  sx={{
                    fontFamily: outfit.style.fontFamily,
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.75)",
                    mb: 0.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.25,
                  }}
                >
                  {t("contactMain.step2.websiteLabel")}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                  <Tooltip
                    title={t("contactMain.step2.websiteTooltip")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{
                        p: 0.25,
                        color: "rgba(255,255,255,0.4)",
                        "&:hover": { color: CARD.white },
                      }}
                      tabIndex={-1}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  name="companyWebsite"
                  value={formData.companyWebsite}
                  onChange={handleChange}
                  placeholder={t("contactMain.step2.websitePlaceholder")}
                  disabled={loading}
                  error={!!errors.companyWebsite}
                  helperText={errors.companyWebsite}
                  inputProps={{
                    style: { textAlign: isArabic ? "right" : "left" },
                  }}
                  sx={darkInput(!!errors.companyWebsite, outfit)}
                />
              </>
            )}

            {/* ── Step 3 ── */}
            {currentStep === 3 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step3.headline")}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    color: CARD.muted,
                    fontFamily: outfit.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.selectAll")}
                </Typography>
                {[
                  t("contactMain.step3.opt1"),
                  t("contactMain.step3.opt2"),
                  t("contactMain.step3.opt3"),
                  t("contactMain.step3.opt4"),
                  "other",
                ].map((opt) => (
                  <Box key={opt}>
                    <CheckRow
                      label={opt === "other" ? t("contactMain.other") : opt}
                      checked={formData.hiringSlowdowns.includes(opt)}
                      onChange={() =>
                        handleChange({
                          target: {
                            name: "hiringSlowdowns",
                            value: opt,
                            checked: !formData.hiringSlowdowns.includes(opt),
                            type: "checkbox",
                          },
                        })
                      }
                    />
                    {opt === "other" && showHiringSlowdownsOther && (
                      <TextField
                        name="hiringSlowdownsOther"
                        value={formData.hiringSlowdownsOther}
                        onChange={handleChange}
                        placeholder={t("contactMain.pleaseSpecify")}
                        disabled={loading}
                        error={!!errors.hiringSlowdownsOther}
                        helperText={errors.hiringSlowdownsOther}
                        inputProps={{
                          style: { textAlign: isArabic ? "right" : "left" },
                        }}
                        sx={{
                          ...darkInput(!!errors.hiringSlowdownsOther, outfit),
                          ml: isArabic ? 0 : 5,
                          mr: isArabic ? 5 : 0,
                          mt: 1,
                          width: "calc(100% - 52px)",
                        }}
                      />
                    )}
                  </Box>
                ))}
                {errors.hiringSlowdowns && (
                  <Typography
                    sx={{ color: CARD.error, fontSize: "12px", mt: 1 }}
                  >
                    {errors.hiringSlowdowns}
                  </Typography>
                )}
              </>
            )}

            {/* ── Step 4 ── */}
            {currentStep === 4 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step4.headline")}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                </Typography>
                {[
                  t("contactMain.step4.opt1"),
                  t("contactMain.step4.opt2"),
                  t("contactMain.step4.opt3"),
                  t("contactMain.step4.opt4"),
                  t("contactMain.step4.opt5"),
                ].map((opt) => (
                  <CheckRow
                    key={opt}
                    label={opt}
                    checked={formData.monthlyHires === opt}
                    onChange={() => {
                      setFormData((p) => ({
                        ...p,
                        monthlyHires: formData.monthlyHires === opt ? "" : opt,
                      }));
                      if (errors.monthlyHires)
                        setErrors((p) => ({ ...p, monthlyHires: "" }));
                    }}
                  />
                ))}
                {errors.monthlyHires && (
                  <Typography
                    sx={{ color: CARD.error, fontSize: "12px", mt: 1 }}
                  >
                    {errors.monthlyHires}
                  </Typography>
                )}
              </>
            )}

            {/* ── Step 5 ── */}
            {currentStep === 5 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step5.headline")}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    color: CARD.muted,
                    fontFamily: outfit.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.selectAll")}
                </Typography>
                {[
                  t("contactMain.step5.opt1"),
                  t("contactMain.step5.opt2"),
                  t("contactMain.step5.opt3"),
                  t("contactMain.step5.opt4"),
                  t("contactMain.step5.opt5"),
                  t("contactMain.step5.opt6"),
                ].map((opt) => (
                  <CheckRow
                    key={opt}
                    label={opt}
                    checked={formData.findingCandidates.includes(opt)}
                    onChange={() =>
                      handleChange({
                        target: {
                          name: "findingCandidates",
                          value: opt,
                          checked: !formData.findingCandidates.includes(opt),
                          type: "checkbox",
                        },
                      })
                    }
                  />
                ))}
                {errors.findingCandidates && (
                  <Typography
                    sx={{ color: CARD.error, fontSize: "12px", mt: 1 }}
                  >
                    {errors.findingCandidates}
                  </Typography>
                )}
              </>
            )}

            {/* ── Step 6 ── */}
            {currentStep === 6 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step6.headline")}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    color: CARD.muted,
                    fontFamily: outfit.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.selectAll")}
                </Typography>
                {[
                  t("contactMain.step6.opt1"),
                  t("contactMain.step6.opt2"),
                  t("contactMain.step6.opt3"),
                  t("contactMain.step6.opt4"),
                  t("contactMain.step6.opt5"),
                  "other",
                ].map((opt) => (
                  <Box key={opt}>
                    <CheckRow
                      label={opt === "other" ? t("contactMain.other") : opt}
                      checked={formData.screeningCandidates.includes(opt)}
                      onChange={() =>
                        handleChange({
                          target: {
                            name: "screeningCandidates",
                            value: opt,
                            checked:
                              !formData.screeningCandidates.includes(opt),
                            type: "checkbox",
                          },
                        })
                      }
                    />
                    {opt === "other" && showScreeningCandidatesOther && (
                      <TextField
                        name="screeningCandidatesOther"
                        value={formData.screeningCandidatesOther}
                        onChange={handleChange}
                        placeholder={t("contactMain.pleaseSpecify")}
                        disabled={loading}
                        error={!!errors.screeningCandidatesOther}
                        helperText={errors.screeningCandidatesOther}
                        inputProps={{
                          style: { textAlign: isArabic ? "right" : "left" },
                        }}
                        sx={{
                          ...darkInput(
                            !!errors.screeningCandidatesOther,
                            outfit,
                          ),
                          ml: isArabic ? 0 : 5,
                          mr: isArabic ? 5 : 0,
                          mt: 1,
                          width: "calc(100% - 52px)",
                        }}
                      />
                    )}
                  </Box>
                ))}
                {errors.screeningCandidates && (
                  <Typography
                    sx={{ color: CARD.error, fontSize: "12px", mt: 1 }}
                  >
                    {errors.screeningCandidates}
                  </Typography>
                )}
              </>
            )}

            {/* ── Step 7 ── */}
            {currentStep === 7 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step7.headline", {
                    name: formData.firstName || t("contactMain.step2.there"),
                  })}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                </Typography>
                {[
                  t("contactMain.step7.opt1"),
                  t("contactMain.step7.opt2"),
                  t("contactMain.step7.opt3"),
                  t("contactMain.step7.opt4"),
                  t("contactMain.step7.opt5"),
                  t("contactMain.step7.opt6"),
                ].map((opt) => (
                  <CheckRow
                    key={opt}
                    label={opt}
                    checked={formData.timeToHireValue === opt}
                    onChange={() => {
                      setFormData((p) => ({
                        ...p,
                        timeToHireValue:
                          formData.timeToHireValue === opt ? "" : opt,
                      }));
                      if (errors.timeToHireValue)
                        setErrors((p) => ({ ...p, timeToHireValue: "" }));
                    }}
                  />
                ))}
                {errors.timeToHireValue && (
                  <Typography
                    sx={{ color: CARD.error, fontSize: "12px", mt: 1 }}
                  >
                    {errors.timeToHireValue}
                  </Typography>
                )}
              </>
            )}

            {/* ── Step 8 ── */}
            {currentStep === 8 && (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: CARD.white,
                    fontFamily: plusJakartaSans.style.fontFamily,
                    mb: 3,
                    fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("contactMain.step8.headline")}{" "}
                  <Box component="span" sx={{ color: CARD.error }}>
                    *
                  </Box>
                </Typography>
                {[
                  t("contactMain.step8.opt1"),
                  t("contactMain.step8.opt2"),
                  t("contactMain.step8.opt3"),
                  t("contactMain.step8.opt4"),
                ].map((opt) => (
                  <CheckRow
                    key={opt}
                    label={opt}
                    checked={formData.urgency === opt}
                    onChange={() => {
                      setFormData((p) => ({
                        ...p,
                        urgency: formData.urgency === opt ? "" : opt,
                      }));
                      if (errors.urgency)
                        setErrors((p) => ({ ...p, urgency: "" }));
                    }}
                  />
                ))}
                {errors.urgency && (
                  <Typography
                    sx={{ color: CARD.error, fontSize: "12px", mt: 1 }}
                  >
                    {errors.urgency}
                  </Typography>
                )}
              </>
            )}

            {/* ── Step 9: Cal.com + Thank you ── */}
            {currentStep === 9 && (
              <>
                {!meetingBooked ? (
                  <Box sx={{ minHeight: 200 }} />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      py: { xs: 6, sm: 8, md: 10 },
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "80px", sm: "100px", md: "120px" },
                        height: { xs: "80px", sm: "100px", md: "120px" },
                        borderRadius: "50%",
                        backgroundColor: "rgba(16,185,129,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        animation: "scaleIn 0.5s ease-out",
                        "@keyframes scaleIn": {
                          "0%": { transform: "scale(0)", opacity: 0 },
                          "50%": { transform: "scale(1.1)" },
                          "100%": { transform: "scale(1)", opacity: 1 },
                        },
                      }}
                    >
                      <Box
                        component="svg"
                        sx={{
                          width: { xs: "50px", sm: "60px", md: "70px" },
                          height: { xs: "50px", sm: "60px", md: "70px" },
                          stroke: "#10B981",
                          fill: "none",
                          strokeWidth: 3,
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                        }}
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M20 6L9 17l-5-5"
                          strokeDasharray="50"
                          strokeDashoffset="0"
                        />
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: plusJakartaSans.style.fontFamily,
                        fontSize: {
                          xs: "1.75rem",
                          sm: "2.25rem",
                          md: "2.75rem",
                        },
                        fontWeight: "bold",
                        color: CARD.white,
                        mb: 3,
                      }}
                    >
                      {t("contactMain.step9.thankYou", {
                        name:
                          formData.firstName || t("contactMain.step2.there"),
                      })}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: outfit.style.fontFamily,
                        fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                        color: "rgba(255,255,255,0.65)",
                        mb: 1.5,
                        lineHeight: 1.6,
                      }}
                    >
                      {t("contactMain.step9.scheduled")}
                    </Typography>
                    {meetingDate && (
                      <Typography
                        sx={{
                          fontFamily: outfit.style.fontFamily,
                          fontSize: {
                            xs: "1rem",
                            sm: "1.125rem",
                            md: "1.25rem",
                          },
                          color: CARD.white,
                          fontWeight: 600,
                          mb: 1.5,
                        }}
                      >
                        {new Date(meetingDate).toLocaleDateString(undefined, {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                        {" at "}
                        {new Date(meetingDate).toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontFamily: outfit.style.fontFamily,
                        fontSize: {
                          xs: "0.875rem",
                          sm: "1rem",
                          md: "1.125rem",
                        },
                        color: "rgba(255,255,255,0.45)",
                        lineHeight: 1.6,
                      }}
                    >
                      {t("contactMain.step9.confirmation")}
                    </Typography>
                  </Box>
                )}

                {/* Cal.com Modal */}
                <Modal
                  open={currentStep === 9 && !meetingBooked}
                  onClose={() => setShowPickSlotHint(true)}
                  disableEscapeKeyDown
                  disableAutoFocus
                  hideBackdrop={false}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: { xs: 1, sm: 2 },
                    "& .MuiBackdrop-root": {
                      backgroundColor: "rgba(0,0,0,0.5)",
                      backdropFilter: "blur(6px)",
                    },
                  }}
                  slotProps={{
                    backdrop: {
                      sx: { cursor: "pointer" },
                      onClick: () => setShowPickSlotHint(true),
                    },
                  }}
                >
                  <Box
                    onClick={() => setShowPickSlotHint(false)}
                    sx={{
                      width: "100%",
                      maxWidth: { xs: "100%", sm: "95%", md: 1100 },
                      height: { xs: "90vh", sm: "85vh", md: "80vh" },
                      bgcolor: "white",
                      borderRadius: "12px",
                      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "default",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        border: showPickSlotHint
                          ? `3px solid ${CARD.purple}`
                          : "3px solid transparent",
                        borderRadius: "8px",
                        transition: "border-color 0.2s ease",
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ flex: 1, minHeight: 0 }}>
                        <iframe
                          src={`https://cal.com/abdul-hannan-8qbqek/30min?overlayCalendar=true&_=${calIframeKey}${formData.email ? `&email=${encodeURIComponent(formData.email.trim())}` : ""}${formData.firstName ? `&name=${encodeURIComponent(formData.firstName.trim())}` : ""}`}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          title="Book your demo with Cal.com"
                          style={{
                            border: "none",
                            display: "block",
                            width: "100%",
                            height: "100%",
                            borderRadius: "6px",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Modal>

                <Snackbar
                  open={currentStep === 9 && !meetingBooked && showPickSlotHint}
                  autoHideDuration={5000}
                  onClose={() => setShowPickSlotHint(false)}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  sx={{ mt: 2 }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderRadius: "8px",
                      borderLeft: `4px solid ${CARD.purple}`,
                      bgcolor: "white",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: outfit.style.fontFamily,
                        fontSize: "0.9375rem",
                        color: CARD.purple,
                        fontWeight: 600,
                      }}
                    >
                      {t("contactMain.step9.pickSlot")}
                    </Typography>
                  </Paper>
                </Snackbar>
              </>
            )}

            {/* ── Navigation Buttons ── */}
            {currentStep < 9 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  mt: 3,
                  alignItems: "center",
                  justifyContent: "flex-end",
                  flexDirection: isArabic ? "row-reverse" : "row",
                }}
              >
                {currentStep > 1 && (
                  <Button
                    onClick={handleBack}
                    variant="text"
                    sx={{
                      color: "rgba(255,255,255,0.45)",
                      textTransform: "none",
                      fontFamily: outfit.style.fontFamily,
                      fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                      fontWeight: 500,
                      px: { xs: 2, sm: 2.5, md: 3 },
                      py: { xs: 0.875, sm: 1, md: 1.125 },
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: CARD.white,
                      },
                    }}
                  >
                    {t("contactMain.back")}
                  </Button>
                )}
                <Button
                  onClick={currentStep === 1 ? handleSubmit : handleNext}
                  disabled={currentStep === 1 ? isStep1Disabled : loading}
                  variant="contained"
                  fullWidth={currentStep === 1}
                  sx={{
                    bgcolor: CARD.white,
                    color: CARD.bg,
                    fontFamily: outfit.style.fontFamily,
                    py: { xs: 0.875, sm: 1, md: 1.125 },
                    px: { xs: 3, sm: 3.5, md: 4 },
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                    fontWeight: 600,
                    minWidth:
                      currentStep > 1
                        ? { xs: "100px", sm: "110px", md: "120px" }
                        : "100%",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#f3f4f6", boxShadow: "none" },
                    "&:disabled": {
                      bgcolor: "rgba(255,255,255,0.12)",
                      color: "rgba(255,255,255,0.25)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {loading || emailVerifying ? (
                    <CircularProgress size={18} sx={{ color: CARD.bg }} />
                  ) : currentStep === 8 ? (
                    t("contactMain.continue")
                  ) : (
                    t("contactMain.next")
                  )}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </GoogleCaptchaWrapper>
  );
};

export default ArticleSixContact;
